import { defineNuxtPlugin, useRequestEvent, useRequestURL } from '#app'

// Forwards the cache-bypass and preview signals from a PAGE URL down to every
// internal `/api/*` request. A visit to `/some/page?no-cache` (or `?preview` /
// `?_storyblok`) must serve fresh data, but the page's SSR fetches build their
// own `/api/...` URLs from slugs/uuids and don't carry the page query. Rather
// than forward it at ~30 call sites, the auto-imported `$fetch` (see the
// fetch.mjs template override in nuxt.config.ts) routes through the instances
// installed here. The server `defineCachedApi` wrapper + `internalFetch`
// fan-out then bypass the KV cache (and fetch draft content, for `preview`)
// all the way through. `preview`/`_storyblok` implies `no-cache` so a draft
// response can never be written to the shared KV cache.
function previewFlags(search) {
    const flags = {}
    if (search.has('no-cache') || search.has('preview') || search.has('_storyblok')) {
        flags['no-cache'] = ''
    }
    if (search.has('preview') || search.has('_storyblok')) {
        flags.preview = ''
    }
    return flags
}

export default defineNuxtPlugin((nuxtApp) => {
    const base = globalThis.$fetch

    if (!base) {
        return
    }

    if (import.meta.server) {
        // NEVER patch globalThis on the server: concurrent SSR requests share
        // it, so a per-request patch leaks one visitor's preview/no-cache
        // flags into another visitor's fetches — and from there into the KV
        // cache. Instead, expose a per-request instance on the nuxtApp; the
        // fetch.mjs template override resolves it at call time.
        const event = useRequestEvent(nuxtApp)
        const flags = previewFlags(useRequestURL().searchParams)

        nuxtApp._apiFetch = base.create({
            onRequest({ request, options }) {
                if (typeof request !== 'string' || !request.startsWith('/api/')) {
                    return
                }
                // Thread the request's H3 context into the nested /api event.
                // On Workers it carries `context.waitUntil` (the Cloudflare
                // execution context); without it, nitro's cache layer has no
                // working `event.waitUntil`, the runtime cancels the KV write
                // as soon as the page response is sent, and "cached" endpoints
                // hit Storyblok on every render. Same mechanism as nitro's own
                // `event.$fetch` / h3 `fetchWithEvent`.
                if (event && !options.context) {
                    options.context = event.context
                }
                if (Object.keys(flags).length > 0) {
                    options.query = { ...options.query, ...flags }
                }
            },
        })
        return
    }

    // Client: a single app instance per tab, so patching the global is safe.
    // The page query changes per navigation, so read `window.location` at
    // request time inside the interceptor.
    globalThis.$fetch = base.create({
        onRequest({ request, options }) {
            if (typeof request !== 'string' || !request.startsWith('/api/')) {
                return
            }

            const flags = previewFlags(new URLSearchParams(window.location.search))

            if (Object.keys(flags).length > 0) {
                options.query = { ...options.query, ...flags }
            }
        },
    })
})
